/**
 * Enterprise maintenance view: operational, clear, trustworthy.
 * No marketing feel. Solid dark background, clean card, status badge, countdown, role-based note.
 */

import { useState, useEffect } from 'react'
import type { LucideIcon } from 'lucide-react'
import { Server } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface MaintenanceViewData {
  message?: string
  announcementMessage?: string
  expectedUptime?: string
  startAt?: string
  endAt?: string
  /** maintenance_mode from API – system is in maintenance */
  maintenanceMode?: boolean
  /** maintenance_schedule_enabled from API */
  scheduleEnabled?: boolean
  /** maintenance_announcement_before_minutes */
  announcementBeforeMinutes?: number
}

export type MaintenanceStatus = 'scheduled' | 'ongoing' | 'expired'

export interface MaintenanceViewProps {
  data: MaintenanceViewData
  /** User has allowed role (show access note, optionally show login) */
  hasAccess?: boolean
  /** When true (e.g. login page), show Login button even during maintenance so allowed users can try */
  allowLoginDuringMaintenance?: boolean
  primaryAction?: { label: string; onClick: () => void; loading?: boolean; icon?: LucideIcon }
  className?: string
  /** Called when maintenance window has expired (endAt in past) – parent may unlock */
  onExpired?: () => void
  /** Override title (e.g. server-down: "System temporarily unavailable") */
  title?: string
  /** Override subtitle (e.g. server-down: "Service temporarily unavailable") */
  subtitle?: string
  /** When true (e.g. global MaintenanceScreen), always show primary action (Retry). When false, only show Login when allowed by spec. */
  alwaysShowPrimaryAction?: boolean
}

const CARD_MAX_WIDTH = 520
const PADDING = 24
const SECTION_GAP = 16

function formatWindowDateTime(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
    return `${date} · ${time}`
  } catch {
    return iso
  }
}

function getStatus(
  maintenanceMode: boolean,
  scheduleEnabled: boolean,
  startAt: string | undefined,
  endAt: string | undefined
): MaintenanceStatus | null {
  const now = Date.now()
  const start = startAt ? new Date(startAt).getTime() : 0
  const end = endAt ? new Date(endAt).getTime() : 0

  if (maintenanceMode) {
    if (end && now > end) return 'expired'
    if (start && end && now >= start && now <= end) return 'ongoing'
    if (start && now < start) return 'scheduled'
    return 'ongoing'
  }
  if (scheduleEnabled && start && end && now < start) return 'scheduled'
  return null
}

function useCountdown(endAt: string | undefined, onExpired?: () => void): { h: number; m: number; s: number } | null {
  const [parts, setParts] = useState<{ h: number; m: number; s: number } | null>(null)

  useEffect(() => {
    if (!endAt) {
      setParts(null)
      return
    }
    const end = new Date(endAt).getTime()
    const update = () => {
      const now = Date.now()
      const diff = end - now
      if (diff <= 0) {
        setParts(null)
        onExpired?.()
        return
      }
      const h = Math.floor(diff / (1000 * 60 * 60))
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const s = Math.floor((diff % (1000 * 60)) / 1000)
      setParts({ h, m, s })
    }
    update()
    const id = setInterval(update, 1000)
    return () => clearInterval(id)
  }, [endAt, onExpired])

  return parts
}

export function MaintenanceView({
  data,
  hasAccess = false,
  allowLoginDuringMaintenance = false,
  primaryAction,
  className,
  onExpired,
  title: titleOverride,
  subtitle: subtitleOverride,
  alwaysShowPrimaryAction = false,
}: MaintenanceViewProps) {
  const title = titleOverride ?? 'Scheduled Maintenance in Progress'
  const subtitle = subtitleOverride ?? 'System is temporarily unavailable'
  const maintenanceMode = data.maintenanceMode === true
  const scheduleEnabled = data.scheduleEnabled === true
  const startAt = data.startAt
  const endAt = data.endAt

  const status = getStatus(maintenanceMode, scheduleEnabled, startAt, endAt)
  const countdown = useCountdown(endAt, onExpired)

  const now = Date.now()
  const startMs = startAt ? new Date(startAt).getTime() : 0
  const showLoginButton =
    allowLoginDuringMaintenance && maintenanceMode
      ? true
      : !maintenanceMode && scheduleEnabled && startMs && now < startMs
  const showPrimaryAction = alwaysShowPrimaryAction || showLoginButton

  useEffect(() => {
    if (status === 'expired') onExpired?.()
  }, [status, onExpired])

  // Force light mode while maintenance screen is mounted; remove on unmount
  useEffect(() => {
    document.documentElement.classList.add('maintenance-light')
    return () => {
      document.documentElement.classList.remove('maintenance-light')
    }
  }, [])

  if (status === 'expired') {
    return null
  }

  const displayMessage = data.announcementMessage || data.message || 'System is temporarily unavailable for scheduled maintenance.'

  return (
    <div
      className={cn('maintenance-screen-light min-h-screen flex flex-col items-center justify-center p-6 relative', className)}
      style={{ background: 'hsl(220 14% 97%)' }}
      role="alert"
      aria-live="assertive"
    >
      {/* Subtle animated grid behind content */}
      <div
        className="absolute inset-0 pointer-events-none maintenance-grid-bg"
        aria-hidden
      />

      {/* Status badge above card with live dot */}
      {status && (
        <div
          className={cn(
            'relative z-10 mb-4 px-3 py-1.5 rounded-full text-xs font-medium flex items-center justify-center',
            status === 'ongoing' && 'bg-[hsl(var(--intent-warning)/0.15)] text-[hsl(var(--intent-warning))]',
            status === 'scheduled' && 'bg-[hsl(var(--intent-info)/0.15)] text-[hsl(var(--intent-info))]'
          )}
        >
          <span className="live-dot" aria-hidden />
          Status: {status === 'ongoing' ? 'Ongoing' : 'Scheduled'}
        </div>
      )}

      <div
        className={cn(
          'relative z-10 w-full rounded-2xl border shadow-xl maintenance-card-breathe',
          'bg-white border-[hsl(220_14%_90%)]'
        )}
        style={{
          maxWidth: CARD_MAX_WIDTH,
          padding: PADDING,
        }}
      >
        <div className="flex flex-col gap-4" style={{ gap: SECTION_GAP }}>
          {/* Header: static Server icon + title + subtitle + process sweep bar */}
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="flex justify-center text-[hsl(var(--intent-processing))] opacity-80">
              <Server className="h-10 w-10" strokeWidth={1.5} aria-hidden />
            </div>
            <h1 className="text-[20px] font-semibold leading-tight text-[hsl(220_14%_12%)]">
              {title}
            </h1>
            <p className="text-sm text-muted-foreground">{subtitle}</p>
            <div className="maintenance-progress-bar">
              <div className="maintenance-progress-sweep" />
            </div>
          </div>

          {/* Message block */}
          <div className="text-center">
            <p className="text-sm text-foreground leading-relaxed">{displayMessage}</p>
          </div>

          {/* Maintenance window (if scheduled with start/end) */}
          {(startAt || endAt) && (
            <div className="rounded-lg border border-[hsl(var(--border-medium))] dark:border-border bg-muted/30 px-4 py-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2 border-b border-[hsl(var(--border-strong))] dark:border-border pb-2">
                Maintenance Window
              </p>
              <div className="text-sm text-foreground space-y-1">
                {startAt && (
                  <p>
                    Start: <span className="font-medium">{formatWindowDateTime(startAt)}</span>
                  </p>
                )}
                {endAt && (
                  <p>
                    End: <span className="font-medium">{formatWindowDateTime(endAt)}</span>
                  </p>
                )}
                <p className="text-xs text-muted-foreground pt-1">Timezone: Your local time</p>
              </div>
            </div>
          )}

          {/* Countdown with soft pulse */}
          {countdown !== null && (
            <div
              className="flex flex-col items-center gap-2 rounded-lg px-4 py-4 maintenance-countdown-pulse bg-[hsl(var(--intent-processing)/0.12)]"
              aria-live="polite"
            >
              <p className="text-sm text-muted-foreground">Resuming in:</p>
              <div
                className="flex items-baseline justify-center gap-1 text-[hsl(var(--intent-processing))] font-semibold tabular-nums font-mono text-2xl"
                aria-live="assertive"
              >
                {String(countdown.h).padStart(2, '0')}h : {String(countdown.m).padStart(2, '0')}m :{' '}
                {String(countdown.s).padStart(2, '0')}s
              </div>
            </div>
          )}

          {/* Role-based access note */}
          {hasAccess && (
            <p className="text-center text-xs text-muted-foreground">You have access during maintenance.</p>
          )}

          {/* Primary action: Home, Retry, etc. (with optional icon) */}
          {showPrimaryAction && primaryAction && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={primaryAction.onClick}
                disabled={primaryAction.loading}
                variant="default"
                className="min-w-[120px]"
              >
                {primaryAction.icon && (
                  <primaryAction.icon className="h-4 w-4 mr-2 shrink-0" aria-hidden />
                )}
                {primaryAction.loading ? 'Please wait...' : primaryAction.label}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
